import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EVENT_LABELS,
  fmtDate,
  fmtDateTime,
  fmtTime,
  REASON_LABELS,
  type CorrectionChange,
  type CorrectionDTO,
  type CorrectionStatus,
  type EventDTO,
} from '@em/shared';
import { api, invalidateAttendance, isApiError, useCorrections, useEvents, useSettings } from '../../api';
import { useSession } from '../../auth/AuthContext';
import { CorrectionDialog } from '../../components/CorrectionDialog';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, Loading, PageHeader, QueryState, Tabs } from '../../components/ui';

const STATUS_TONE = { pending: 'amber', approved: 'green', rejected: 'red' } as const;

function describe(ch: CorrectionChange, events: Map<string, EventDTO> | null, tz: string): string {
  if (ch.action === 'add') return `Add ${EVENT_LABELS[ch.type].toLowerCase()} at ${fmtTime(ch.occurredAt, tz)}${ch.breakKind ? ` (${ch.breakKind})` : ''}`;
  const ev = events?.get(ch.eventId);
  const what = ev ? `${EVENT_LABELS[ev.type].toLowerCase()} at ${fmtTime(ev.occurredAt, tz)}` : 'a punch';
  if (ch.action === 'change') return `Move ${what} to ${fmtTime(ch.occurredAt, tz)}`;
  return `Void ${what}`;
}

export function CorrectionsPage() {
  const [status, setStatus] = useState<CorrectionStatus>('pending');
  const q = useCorrections({ status });
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const session = useSession();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [approving, setApproving] = useState<CorrectionDTO | null>(null);
  const [rejecting, setRejecting] = useState<CorrectionDTO | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [rerouted, setRerouted] = useState<string | null>(null);

  const isMine = (c: CorrectionDTO) => c.requestedByUserId === session.user.id || c.employeeId === session.user.employeeId;

  return (
    <>
      <PageHeader
        title="Corrections"
        subtitle="Changes to punches. Each needs approval by someone other than the person who asked for it or whose time it is."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New correction
          </Button>
        }
      />
      {rerouted && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="amber" title="Sent to another approver">
            {rerouted}
          </Alert>
        </div>
      )}
      <Tabs
        label="Correction status"
        value={status}
        onChange={(s) => {
          setStatus(s);
          setExpanded(null);
        }}
        items={[
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
        ]}
      />
      <Card tight>
        <QueryState q={q} loadingLabel="Loading corrections">
          {(list) =>
            list.length === 0 ? (
              <EmptyState title={status === 'pending' ? 'Nothing waiting for approval' : `No ${status} corrections`} />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Shift date</th>
                      <th>Changes</th>
                      <th>Reason</th>
                      <th>Requested</th>
                      <th>{status === 'pending' ? 'Status' : 'Decision'}</th>
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c) => (
                      <Fragment key={c.id}>
                        <tr>
                          <td className="strong">
                            <Link to={`/employees/${c.employeeId}`}>{c.employeeName}</Link>
                          </td>
                          <td className="nowrap">{fmtDate(c.shiftDate)}</td>
                          <td>
                            {expanded === c.id ? (
                              <span className="muted small">See below</span>
                            ) : (
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {c.changes.map((ch, i) => (
                                  <li key={i}>{describe(ch, null, tz)}</li>
                                ))}
                              </ul>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === c.id ? null : c.id)} aria-expanded={expanded === c.id}>
                              {expanded === c.id ? 'Hide details' : 'Details'}
                            </Button>
                          </td>
                          <td>
                            {REASON_LABELS[c.reasonCode]}
                            {c.note && <div className="small muted">“{c.note}”</div>}
                          </td>
                          <td className="small">
                            {c.requestedByName}
                            {c.requestedByEmployee && <Badge tone="accent">From app</Badge>}
                            <div className="muted">{fmtDateTime(c.createdAt, tz)}</div>
                          </td>
                          <td className="small">
                            <Badge tone={STATUS_TONE[c.status]}>{c.status === 'pending' ? 'Pending' : c.status === 'approved' ? 'Approved' : 'Rejected'}</Badge>
                            {c.appliedAsAdjustment && (
                              <div>
                                <Badge tone="amber">Next-period adjustment</Badge>
                              </div>
                            )}
                            {c.decidedByName && (
                              <div className="muted">
                                {c.decidedByName}
                                {c.decidedAt ? `, ${fmtDateTime(c.decidedAt, tz)}` : ''}
                              </div>
                            )}
                            {c.decisionNote && <div>“{c.decisionNote}”</div>}
                          </td>
                          <td className="actions">
                            {c.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => setApproving(c)}
                                  title={isMine(c) ? 'You requested this or it is your own time; another approver must decide' : undefined}
                                >
                                  Approve
                                </Button>
                                <Button size="sm" variant="danger" onClick={() => setRejecting(c)}>
                                  Reject
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                        {expanded === c.id && (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--surface-2)' }}>
                              <CorrectionDetail c={c} tz={tz} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </QueryState>
      </Card>

      {creating && <CorrectionDialog onClose={() => setCreating(false)} onCreated={() => setStatus('pending')} />}
      {approving && (
        <PromptDialog
          title="Approve correction"
          description={
            <>
              <strong>{approving.employeeName}</strong>, {fmtDate(approving.shiftDate)}
              <ul>
                {approving.changes.map((ch, i) => (
                  <li key={i}>{describe(ch, null, tz)}</li>
                ))}
              </ul>
              {isMine(approving) && (
                <Alert tone="amber">You requested this correction or it is for your own time. If you continue it is sent to another approver instead.</Alert>
              )}
            </>
          }
          confirmLabel="Approve"
          onClose={() => setApproving(null)}
          onConfirm={async (note) => {
            try {
              const r = await api.approveCorrection(approving.id, note || undefined);
              invalidateAttendance();
              toast.success('Correction approved', r.appliedAsAdjustment ? 'The period was already locked, so it lands as an adjustment in the next pay run.' : 'The timesheet has been recalculated.');
            } catch (e) {
              if (isApiError(e, 'self_approval')) {
                setRerouted(e.message);
                invalidateAttendance();
                return;
              }
              throw e;
            }
          }}
        />
      )}
      {rejecting && (
        <PromptDialog
          title="Reject correction"
          description={`${rejecting.employeeName} is told why. The punches stay as they are.`}
          label="Reason for rejecting"
          required
          danger
          confirmLabel="Reject"
          onClose={() => setRejecting(null)}
          onConfirm={async (note) => {
            await api.rejectCorrection(rejecting.id, note);
            invalidateAttendance();
            toast.success('Correction rejected');
          }}
        />
      )}
    </>
  );
}

function CorrectionDetail({ c, tz }: { c: CorrectionDTO; tz: string }) {
  const events = useEvents({ employeeId: c.employeeId, from: c.shiftDate, to: c.shiftDate });
  if (events.isPending) return <Loading />;
  const map = new Map((events.data ?? []).map((e) => [e.id, e]));
  return (
    <div className="grid grid-2">
      <div>
        <h3 style={{ marginBottom: 6 }}>Requested changes</h3>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {c.changes.map((ch, i) => (
            <li key={i}>{describe(ch, map, tz)}</li>
          ))}
        </ul>
      </div>
      <div>
        <h3 style={{ marginBottom: 6 }}>Punches recorded that day</h3>
        {(events.data ?? []).length === 0 ? (
          <span className="muted">None</span>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {events.data!.map((e) => (
              <li key={e.id} style={e.status !== 'active' ? { textDecoration: 'line-through', color: 'var(--text-3)' } : undefined}>
                {EVENT_LABELS[e.type]} {fmtTime(e.occurredAt, tz)} <span className="muted small">({e.source}{e.status !== 'active' ? `, ${e.status}` : ''})</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
